import { Search, Star } from "lucide-react";
import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
  type KeyboardEvent, type ReactNode,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { buscarGlobal, getFavoritos, guardarFavoritos, type BusquedaGrupo } from "@/lib/api";

/*
 * Piezas de la barra superior (calidad de vida, 27-oct-2026):
 *   - Favoritos: estrella en cada opción de menú; las marcadas aparecen en un
 *     menú "Favoritos" al inicio. Desde el 28-oct-2026 se guardan en la BD por
 *     usuario (mig 127) para que sigan al usuario en cualquier equipo; el
 *     localStorage queda como caché y como origen de la migración inicial.
 *   - Búsqueda global (Ctrl+K): contratos, contrapartes, facturas, oficios,
 *     garantías, participantes y portafolio desde un solo cuadro.
 */

// ------------------------------------------------------------ Favoritos
const FAV_KEY = "etrm.favoritos";

type FavCtx = { favoritos: string[]; esFav: (to: string) => boolean; toggle: (to: string) => void };
const Ctx = createContext<FavCtx>({ favoritos: [], esFav: () => false, toggle: () => {} });

function leerFavoritos(): string[] {
  try {
    const v = JSON.parse(localStorage.getItem(FAV_KEY) ?? "[]");
    return Array.isArray(v) ? v.filter((x) => typeof x === "string") : [];
  } catch {
    return [];
  }
}
function cachearFavoritos(rutas: string[]) {
  try { localStorage.setItem(FAV_KEY, JSON.stringify(rutas)); } catch { /* sin storage */ }
}

export function FavoritosProvider({ children }: { children: ReactNode }) {
  // Arranca con la caché local (sin parpadeo) y se sincroniza con el servidor.
  const [favoritos, setFavoritos] = useState<string[]>(leerFavoritos);

  useEffect(() => {
    let vivo = true;
    getFavoritos()
      .then((f) => {
        if (!vivo) return;
        if (f.rutas.length > 0 || !f.persistido) {
          setFavoritos(f.rutas.length > 0 ? f.rutas : leerFavoritos());
          if (f.rutas.length > 0) cachearFavoritos(f.rutas);
          return;
        }
        // Servidor vacío y persistido: migrar lo que tenga este navegador (una vez).
        const locales = leerFavoritos();
        if (locales.length > 0) guardarFavoritos(locales).catch(() => { /* se reintenta al siguiente toggle */ });
      })
      .catch(() => { /* sin sesión o sin red: se queda la caché local */ });
    return () => { vivo = false; };
  }, []);

  const toggle = useCallback((to: string) => {
    setFavoritos((prev) => {
      const next = prev.includes(to) ? prev.filter((x) => x !== to) : [...prev, to];
      cachearFavoritos(next);
      guardarFavoritos(next)
        .then((f) => { setFavoritos(f.rutas); cachearFavoritos(f.rutas); })
        .catch(() => { /* la caché local conserva el cambio; se reintenta al siguiente toggle */ });
      return next;
    });
  }, []);
  const value = useMemo(
    () => ({ favoritos, esFav: (to: string) => favoritos.includes(to), toggle }),
    [favoritos, toggle],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export const useFavoritos = () => useContext(Ctx);

/** Estrella al lado de una opción de menú (no navega: solo marca/desmarca). */
export function EstrellaFav({ to }: { to: string }) {
  const { esFav, toggle } = useFavoritos();
  const on = esFav(to);
  return (
    <button
      type="button"
      onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggle(to); }}
      title={on ? "Quitar de favoritos" : "Agregar a favoritos"}
      className={`ml-2 shrink-0 rounded p-0.5 ${on ? "text-amber-500" : "text-slate-300 hover:text-amber-400"}`}
    >
      <Star className="w-3.5 h-3.5" fill={on ? "currentColor" : "none"} />
    </button>
  );
}

// ------------------------------------------------------------ Búsqueda global
export function BusquedaGlobal() {
  const [q, setQ] = useState("");
  const [abierto, setAbierto] = useState(false);
  const [grupos, setGrupos] = useState<BusquedaGrupo[]>([]);
  const [cargando, setCargando] = useState(false);
  const [sel, setSel] = useState(0);
  const input = useRef<HTMLInputElement>(null);
  const caja = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Ctrl+K / Cmd+K enfoca el cuadro.
  useEffect(() => {
    const onKey = (e: globalThis.KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        input.current?.focus();
        input.current?.select();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);
  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (caja.current && !caja.current.contains(e.target as Node)) setAbierto(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);
  useEffect(() => { setAbierto(false); }, [location.pathname, location.search]);

  // Búsqueda con retardo y cancelación.
  useEffect(() => {
    const texto = q.trim();
    if (texto.length < 2) { setGrupos([]); setCargando(false); return; }
    const ctrl = new AbortController();
    setCargando(true);
    const t = setTimeout(() => {
      buscarGlobal(texto, ctrl.signal)
        .then((r) => { setGrupos(r.grupos); setSel(0); })
        .catch(() => { /* cancelada o error: se deja lo anterior */ })
        .finally(() => { if (!ctrl.signal.aborted) setCargando(false); });
    }, 250);
    return () => { clearTimeout(t); ctrl.abort(); };
  }, [q]);

  const planos = useMemo(() => grupos.flatMap((g) => g.items.map((i) => ({ ...i, grupo: g.label }))), [grupos]);

  const ir = (ruta: string) => {
    setAbierto(false);
    setQ("");
    navigate(ruta);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Escape") { setAbierto(false); input.current?.blur(); return; }
    if (!planos.length) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setSel((s) => (s + 1) % planos.length); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setSel((s) => (s - 1 + planos.length) % planos.length); }
    else if (e.key === "Enter") { e.preventDefault(); ir(planos[sel].ruta); }
  };

  let idx = -1;
  return (
    <div className="relative hidden md:block" ref={caja}>
      <Search className="w-4 h-4 text-slate-400 absolute left-2.5 top-2 pointer-events-none" />
      <input
        ref={input}
        value={q}
        onChange={(e) => { setQ(e.target.value); setAbierto(true); }}
        onFocus={() => setAbierto(true)}
        onKeyDown={onKeyDown}
        placeholder="Buscar (Ctrl+K)…"
        className="border rounded-lg pl-8 pr-3 py-1.5 text-sm w-56 focus:w-80 transition-[width] bg-slate-50 focus:bg-white"
      />
      {abierto && q.trim().length >= 2 && (
        <div className="absolute left-0 mt-1 w-[32rem] max-h-[70vh] overflow-y-auto bg-white border rounded-lg shadow-lg z-40 py-1">
          {cargando && planos.length === 0 && (
            <div className="px-3 py-2 text-sm text-slate-400">Buscando…</div>
          )}
          {!cargando && planos.length === 0 && (
            <div className="px-3 py-2 text-sm text-slate-400">Sin resultados para “{q.trim()}”.</div>
          )}
          {grupos.map((g) => (
            <div key={g.tipo}>
              <div className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-wide text-slate-400">{g.label}</div>
              {g.items.map((it) => {
                idx += 1;
                const activo = idx === sel;
                return (
                  <button
                    key={it.ruta + it.titulo}
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => ir(it.ruta)}
                    className={`w-full text-left px-3 py-1.5 ${activo ? "bg-slate-100" : "hover:bg-slate-50"}`}
                  >
                    <div className="text-sm text-slate-800 truncate">{it.titulo}</div>
                    {it.subtitulo && <div className="text-xs text-slate-500 truncate">{it.subtitulo}</div>}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
